import { useRef, useCallback, useEffect, useState } from 'react';
import type { ViewportInfo } from '../hooks/useFabricCanvas';

/** True when the primary input device is touch (no fine pointer).
 *  Cached at module load — re-evaluating on a per-instance basis isn't
 *  worth the cost; users don't routinely swap input devices mid-session. */
const IS_COARSE_POINTER =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

interface CanvasAreaProps {
  canvasElRef: React.RefObject<HTMLCanvasElement | null>;
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  onFileDropped?: (file: File) => void;
  onFileSelected?: (file: File) => void;
  onOpenFias?: () => void;
  hasImage: boolean;
  /** Renders a CSS-only checkerboard pattern across the viewport
   *  to indicate the transparency-friendly background. Distinct
   *  from the measurement grid feature (CanvasOverlays/Grid) —
   *  this is the underlay checker that shows through transparent
   *  pixels of the image; that's a separate ruler-axis grid. */
  transparencyCheckerVisible: boolean;
  viewport: ViewportInfo;
  /** When set, draws an SVG circle overlay following the mouse to
   *  show the brush footprint at current size. Hidden on touch devices
   *  (no hover concept) and when the cursor leaves the canvas area. */
  brushCursor?: {
    sizePx: number;
    /** Color of the cursor outline. The fill is always transparent so
     *  users can see what's beneath. */
    color: string;
    /** When true, draws a small `+` at the center of the circle —
     *  used for Retouch tools to give the cursor a visible center
     *  point and pair visually with the source-point reticle. */
    withCenterCross?: boolean;
  } | null;
  /** When true, the user is in source-set mode (Clone Stamp armed
   *  via "Set Source" button OR Alt-key held down). The brush
   *  circle is hidden and the wrapper switches to a crosshair
   *  cursor so the gesture's intent is visually distinct from
   *  paint mode. */
  sourceSetMode?: boolean;
  /** Tier 2 Group D workspace overlays — Rulers / Grid / Guides
   *  built in `CanvasOverlays/`. Constructed by EditorPage and
   *  rendered inside the wrapper so absolute positioning resolves
   *  correctly against the canvas viewport. Layered above the
   *  Fabric canvas (z 5-15) and below the brush cursor (z 20). */
  overlays?: React.ReactNode;
}

export function CanvasArea({
  canvasElRef,
  wrapperRef,
  onFileDropped,
  onFileSelected,
  onOpenFias,
  hasImage,
  transparencyCheckerVisible,
  viewport,
  brushCursor,
  sourceSetMode = false,
  overlays,
}: CanvasAreaProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Track cursor position relative to the wrapper for the brush
  // preview overlay. We listen on the wrapper directly so events from
  // the Fabric canvas bubble up correctly. `visible` flips off on
  // mouseleave so the overlay doesn't stick around at the last
  // position when the cursor leaves the canvas area.
  const [cursorPos, setCursorPos] = useState<{ x: number; y: number; visible: boolean }>({
    x: 0,
    y: 0,
    visible: false,
  });
  // Source-set mode hides the brush circle and lets the OS render
  // a crosshair via CSS — the visual swap signals "tap will set
  // source, not paint."
  const showBrushCursor = !!brushCursor && !IS_COARSE_POINTER && !sourceSetMode;
  useEffect(() => {
    if (!showBrushCursor) return;
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const onMove = (e: MouseEvent) => {
      const rect = wrapper.getBoundingClientRect();
      setCursorPos({ x: e.clientX - rect.left, y: e.clientY - rect.top, visible: true });
    };
    const onLeave = () => setCursorPos((p) => ({ ...p, visible: false }));
    wrapper.addEventListener('mousemove', onMove);
    wrapper.addEventListener('mouseleave', onLeave);
    return () => {
      wrapper.removeEventListener('mousemove', onMove);
      wrapper.removeEventListener('mouseleave', onLeave);
    };
  }, [showBrushCursor, wrapperRef]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const file = e.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) {
        onFileDropped?.(file);
      }
    },
    [onFileDropped],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        onFileSelected?.(file);
      }
      e.target.value = '';
    },
    [onFileSelected],
  );

  const handleOpenFileClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const TRACK_SIZE = 8;
  const MIN_THUMB = 24;

  // Compose the wrapper's inline style. Source-set mode adds a
  // CSS `cursor: crosshair` so the OS renders a system crosshair
  // — visually distinct from the brush-circle preview that
  // appears in paint mode. Fabric's per-tool cursor (set inside
  // useFabricCanvas.setTool) gets overridden by this wrapper-
  // level rule, which is correct: source-set mode is a transient
  // state that should win regardless of the underlying tool's
  // default cursor.
  const wrapperStyle: React.CSSProperties = {};
  if (transparencyCheckerVisible) {
    wrapperStyle.backgroundImage =
      'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)';
    wrapperStyle.backgroundSize = '20px 20px';
    wrapperStyle.backgroundPosition = '0 0, 0 10px, 10px -10px, -10px 0px';
  }
  if (sourceSetMode) {
    wrapperStyle.cursor = 'crosshair';
  }

  return (
    <div
      ref={wrapperRef}
      className="relative flex-1 overflow-hidden bg-neutral-200 dark:bg-neutral-800"
      style={wrapperStyle}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      {/* Fabric.js canvas element — Fabric wraps this in its own container */}
      <canvas ref={canvasElRef} />

      {/* Workspace overlays (Tier 2 Group D). Rendered AFTER the
          canvas so they layer above it; each overlay sets its
          own z-index (Grid=5 / Rulers=10 / Guides=15) so the
          brush cursor (z=20) and scrollbars (z=10) stack
          correctly above. */}
      {overlays}

      {/* Empty state: drop zone */}
      {!hasImage && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <div className="p-6 rounded-lg bg-background/80 border-2 border-dashed border-muted-foreground/30 pointer-events-auto flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
              onClick={handleOpenFileClick}
            >
              Open Local Image
            </button>
            <button
              type="button"
              className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md text-sm font-medium hover:bg-secondary/80 transition-colors"
              onClick={() => onOpenFias?.()}
            >
              Open Fias Image
            </button>
            <input
              ref={fileInputRef}
              type="file"
              // PSD / PSB / SVG aren't part of `image/*` (PSD has no
              // registered image/ subtype; browsers vary on SVG), so
              // they wouldn't appear in the picker's default filter
              // without explicit extensions. Matches the accept list
              // used by the main toolbar's open input.
              accept="image/*,.psd,.psb,.svg"
              className="hidden"
              onChange={handleFileInputChange}
            />
          </div>
        </div>
      )}

      {/* Horizontal scrollbar */}
      {viewport.showH && (
        <div
          className="absolute left-0 bottom-0 pointer-events-none z-10"
          style={{
            right: viewport.showV ? TRACK_SIZE : 0,
            height: TRACK_SIZE,
          }}
        >
          <div className="relative w-full h-full bg-black/10 dark:bg-white/10 rounded-full">
            <div
              className="absolute top-0 h-full bg-black/30 dark:bg-white/30 rounded-full"
              style={{
                width: `${Math.max(MIN_THUMB / 100, viewport.thumbWidthRatio * 100)}%`,
                left: `${viewport.scrollXRatio * (1 - viewport.thumbWidthRatio) * 100}%`,
                minWidth: MIN_THUMB,
              }}
            />
          </div>
        </div>
      )}

      {/* Brush cursor preview — pure visual; pointer-events:none so it
          never intercepts clicks that should go to the Fabric canvas. */}
      {showBrushCursor && cursorPos.visible && brushCursor && (
        <svg
          className="absolute pointer-events-none z-20"
          width={brushCursor.sizePx + 4}
          height={brushCursor.sizePx + 4}
          style={{
            left: cursorPos.x - brushCursor.sizePx / 2 - 2,
            top: cursorPos.y - brushCursor.sizePx / 2 - 2,
          }}
          aria-hidden="true"
        >
          <circle
            cx={brushCursor.sizePx / 2 + 2}
            cy={brushCursor.sizePx / 2 + 2}
            r={Math.max(brushCursor.sizePx / 2, 1)}
            fill="none"
            stroke={brushCursor.color}
            strokeWidth="1"
            strokeOpacity="0.9"
            shapeRendering="geometricPrecision"
          />
          <circle
            cx={brushCursor.sizePx / 2 + 2}
            cy={brushCursor.sizePx / 2 + 2}
            r={Math.max(brushCursor.sizePx / 2 + 1, 2)}
            fill="none"
            stroke="black"
            strokeWidth="0.5"
            strokeOpacity="0.5"
            shapeRendering="geometricPrecision"
          />
          {brushCursor.withCenterCross &&
            (() => {
              // Center-cross size scales gently with the brush
              // footprint so the `+` stays readable for both tiny
              // and huge brushes — but bounded so a 200px brush
              // doesn't get a giant cross. White halo + accent
              // stroke matches the source reticle's visual
              // language, keeping the source/destination pair
              // legible against any image.
              const cx = brushCursor.sizePx / 2 + 2;
              const cy = brushCursor.sizePx / 2 + 2;
              const half = Math.min(Math.max(brushCursor.sizePx * 0.18, 4), 9);
              return (
                <g aria-hidden="true">
                  <line
                    x1={cx - half}
                    y1={cy}
                    x2={cx + half}
                    y2={cy}
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1={cx}
                    y1={cy - half}
                    x2={cx}
                    y2={cy + half}
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1={cx - half}
                    y1={cy}
                    x2={cx + half}
                    y2={cy}
                    stroke={brushCursor.color}
                    strokeWidth="1"
                    strokeLinecap="round"
                  />
                  <line
                    x1={cx}
                    y1={cy - half}
                    x2={cx}
                    y2={cy + half}
                    stroke={brushCursor.color}
                    strokeWidth="1"
                    strokeLinecap="round"
                  />
                </g>
              );
            })()}
        </svg>
      )}

      {/* Vertical scrollbar */}
      {viewport.showV && (
        <div
          className="absolute top-0 right-0 pointer-events-none z-10"
          style={{
            bottom: viewport.showH ? TRACK_SIZE : 0,
            width: TRACK_SIZE,
          }}
        >
          <div className="relative w-full h-full bg-black/10 dark:bg-white/10 rounded-full">
            <div
              className="absolute left-0 w-full bg-black/30 dark:bg-white/30 rounded-full"
              style={{
                height: `${Math.max(MIN_THUMB / 100, viewport.thumbHeightRatio * 100)}%`,
                top: `${viewport.scrollYRatio * (1 - viewport.thumbHeightRatio) * 100}%`,
                minHeight: MIN_THUMB,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
